<!-- src/js/widgets/manager-residency-requests/components/RequestsList.vue -->
<template>
  <div class="manager-residency-requests__list-wrapper">
    <div class="manager-residency-requests__header">
      <h2 class="manager-residency-requests__title">Заявки</h2>
    </div>
    <div class="manager-residency-requests__action-bar">
      <SearchInput v-model="search" placeholder="Найти..." @search="handleSearch" />
      <button type="button" class="btn btn-secondary btn-medium manager-residency-requests__filter" @click="filterOpen = true">
        <FilterIcon :filled="isFilterApplied" />
        <span>Фильтр</span>
      </button>
      <button type="button" class="btn btn-secondary btn-medium manager-residency-requests__save-all" @click="onExportClick">
        Скачать заявки <span class="manager-residency-requests__button-note">(.xls)</span>
      </button>
    </div>

    <div class="manager-residency-requests__table-wrapper personal-area-table-wrapper">
      <div v-if="props.requests.length === 0 && !loading" class="manager-residency-requests__list--empty">
        <div class="manager-residency-requests__list--empty-title">{{ emptyState.title }} <span v-if="search.length > 0">«{{ search }}»</span></div>

        <div v-if="emptyState.description" class="manager-residency-requests__list--empty-description">
          {{ emptyState.description }}
        </div>
      </div>

      <DataTable :loading="loading" :value="props.requests" @row-click="onRowClick" dataKey="id" selectionMode="single" tableStyle="min-width: 70rem" scrollable scrollHeight="600px" v-else :rowClass="getRowClass">
        <template #loading>
          <div class="manager-residency-requests__loading">
            <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
            <span>Загрузка...</span>
          </div>
        </template>

        <Column v-for="column in visibleColumns" :key="column.field" :header="column.header">
          <template #body="{ data }">
            <template v-if="column.field === 'educationType'">
              <p v-for="item in data.educationType" :key="item" :class="getRequestRowClass(data, true)">
                {{ item }}
              </p>
            </template>

            <span v-else :class="column.cellClass?.(data) ?? getRequestRowClass(data, true)">
              {{ column.render(data) }}
            </span>
          </template>
        </Column>

      </DataTable>
    </div>

    <Pagination v-model="currentPageProxy" :total-count="pagination.totalCount" :page-size="pagination.pageSize" @change="handlePageChange" />
  </div>

  <!-- Фильтр -->
  <ADrawer :width="800" :open="filterOpen" class="request-filter-drawer" :body-style="{ paddingBottom: '24px' }" :footer-style="{ textAlign: 'left' }" @close="filterOpen = false">
    <template #title>
      <div class="request-filter-drawer__header-shell">
        <div class="request-filter-drawer__header-overline">Фильтр</div>
      </div>
    </template>

    <template #closeIcon>
      <span class="request-filter-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <AForm layout="vertical">
      <div class="manager-residency-requests__filter-fields-wrapper">
        <AFormItem label="Статус" class="stc-ant-field-skin">
          <ASelect v-model:value="filter.status" placeholder="Статус" :options="statusOptions" />
        </AFormItem>

        <AFormItem label="Диапазон поступления" class="stc-ant-field-skin">
          <AConfigProvider :locale="ruRU">
            <ARangePicker v-model:value="dateRange" style="width: 100%" format="DD.MM.YYYY" :placeholder="['дд.мм.гггг', 'дд.мм.гггг']">
              <template #separator>-</template>
              <template #suffixIcon>
                <img src="/assets/images/icons/range-picker-icon.svg" alt="" />
              </template>
            </ARangePicker>
          </AConfigProvider>
        </AFormItem>
      </div>

      <div class="manager-residency-requests__filter-fields-section">
        <div>
          <AFormItem v-if="hasFilterField('businessArea')" label="Сфера деятельности" class="stc-ant-field-skin">
            <ASelect v-model:value="filter.businessArea" placeholder="Сфера деятельности" :options="businessAreaOptions" />
          </AFormItem>
        </div>

        <AFormItem v-if="hasFilterField('rentalProperty')" label="Объект аренды" class="stc-ant-field-skin">
          <ASelect v-model:value="filter.rentalProperty" placeholder="Объект аренды" :options="rentalPropertyOptions" />
        </AFormItem>

        <AFormItem v-if="hasFilterField('cluster')" label="Кластер" class="stc-ant-field-skin">
          <ASelect v-model:value="filter.cluster" placeholder="Кластер" :options="clusterOptions" />
        </AFormItem>

        <AFormItem v-if="hasFilterField('educationType')" label="Вид обр. деятельности" class="stc-ant-field-skin">
          <ASelect v-model:value="filter.educationType" placeholder="Вид обр. деятельности" :options="typeEducationOptions" />
        </AFormItem>
      </div>
    </AForm>

    <template #footer>
      <ARow align="middle" justify="end" class="request-filter-drawer__toolbar">
        <ACol>
          <ASpace>
            <button type="button" class="btn btn-outline" @click="resetFilters">Сбросить</button>
            <button type="button" class="btn btn-primary" @click="applyFilters">Применить</button>
          </ASpace>
        </ACol>
      </ARow>
    </template>
  </ADrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import type { FiltersListResponse, ApiStatus, RawApplication } from '../types';
import { CloseOutlined } from '@ant-design/icons-vue';
import SearchInput from '../../SearchInput.vue';
import Pagination from '../../Pagination.vue';
import FilterIcon from '@/js/components/icons/FilterIcon.vue';
import {
  Drawer as ADrawer,
  Form as AForm,
  FormItem as AFormItem,
  Select as ASelect,
  DatePicker as ADatePicker,
  Row as ARow,
  Col as ACol,
  Space as ASpace,
  ConfigProvider as AConfigProvider,
} from 'ant-design-vue';
import dayjs, { type Dayjs } from 'dayjs';
import ruRU from 'ant-design-vue/es/locale/ru_RU';

const ARangePicker = ADatePicker.RangePicker;

const props = defineProps<{
  requests: RawApplication[];
  pagination: { totalCount: number; pageSize: number; currentPage: number; pageCount: number };
  loading: boolean;
  filter: FiltersListResponse & {
    filtersLoading: boolean;
    filtersLoadingError: string | null;
  };
}>();

const emit = defineEmits<{
  (e: 'row-click', request: RawApplication): void;
  (
    e: 'params-change',
    params: {
      page: number;
      filter: Partial<Record<keyof FiltersListResponse, string | ApiStatus>> & {
        dateFrom: string | null;
        dateTo: string | null;
      };
      search: string;
    },
  ): void;
  (e: 'export-click', params: {
    filter: Partial<Record<keyof FiltersListResponse, string | ApiStatus>> & {
      dateFrom: string | null;
      dateTo: string | null;
    };
    search: string;
  }): void;
}>();

const columns = [
  {
    field: 'id',
    header: '#',
    render: (data: RawApplication) => data.id,
    cellClass: null,
  },
  {
    field: 'createdAt',
    header: 'Дата поступления',
    render: (data: RawApplication) => dayjs(data.createdAt).format('DD.MM.YYYY / HH:mm'),
    cellClass: null,
  },
  {
    field: 'companyName',
    header: 'Компания',
    render: (data: RawApplication) => data.companyName || '',
    cellClass: null,
  },
  {
    field: 'fio',
    header: 'Контактное лицо',
    render: (data: RawApplication) => data.fio || '',
    cellClass: null,
  },
  {
    field: 'phone',
    header: 'Телефон',
    render: (data: RawApplication) => data.phone || '',
    cellClass: null,
  },
  {
    field: 'email',
    header: 'Почта',
    render: (data: RawApplication) => data.email || '',
    cellClass: null,
  },
  {
    field: 'businessArea',
    header: 'Сфера деятельности',
    render: (data: RawApplication) => data.businessArea || '',
    cellClass: null,
  },
  {
    field: 'rentalProperty',
    header: 'Объект аренды',
    render: (data: RawApplication) => data.rentalProperty || '',
    cellClass: null,
  },
  {
    field: 'dateEvent',
    header: 'Желаемая дата мероприятия',
    render: (data: RawApplication) => dayjs(data.dateEvent).format('DD.MM.YYYY / HH:mm'),
    cellClass: null,
  },
  {
    field: 'educationType',
    header: 'Вид образовательной деятельности',
    render: (data: RawApplication) => data.educationType || '',
    cellClass: null,
  },
  {
    field: 'status',
    header: 'Статус',
    render: (data: RawApplication) => data.status.name,
    cellClass: (data: RawApplication) => getStatusClass(data.status.id),
  },
] as const;

const filterOpen = ref(false);
const filter = ref<
  Partial<Record<keyof FiltersListResponse, string | ApiStatus>> & {
    dateFrom: string | null;
    dateTo: string | null;
  }
>({
  status: '',
  dateFrom: null,
  dateTo: null,
  businessArea: '',
  rentalProperty: '',
  cluster: '',
  educationType: '',
});
const search = ref('');
const dateRange = ref<[Dayjs, Dayjs] | undefined>(undefined);

const isFilterApplied = computed(() => {
  return Boolean(
    filter.value.status ||
    filter.value.dateFrom ||
    filter.value.dateTo ||
    filter.value.businessArea ||
    filter.value.rentalProperty ||
    filter.value.cluster ||
    filter.value.educationType,
  );
});

const emptyState = computed(() => {
  const hasSearch = search.value.trim().length > 0;

  if (hasSearch && isFilterApplied.value) {
    return {
      title: `0 результатов для `,
      description: 'Измените параметры поиска или фильтра.',
    };
  }

  if (hasSearch) {
    return {
      title: `0 совпадений для `,
      description: 'Проверьте орфографию или измените запрос.',
    };
  }

  if (isFilterApplied.value) {
    return {
      title: '0 результатов',
      description: 'Измените значения в фильтре.',
    };
  }

  return {
    title: 'Нет поступивших заявок',
    description: '',
  };
});

const visibleColumns = computed(() => {
  const firstRow = props.requests?.[0];

  if (!firstRow) {
    return columns;
  }

  return columns.filter((column) => Object.prototype.hasOwnProperty.call(firstRow, column.field));
});

const hasFilterField = (key: keyof FiltersListResponse) => {
  return Object.prototype.hasOwnProperty.call(props.filter, key);
};

const currentPageProxy = computed({
  get: () => props.pagination.currentPage,
  set: (val: number) => {
    emit('params-change', {
      page: val,
      filter: filter.value,
      search: search.value,
    });
  },
});

const statusOptions = computed(() =>
  props.filter.status?.map((item) => ({
    value: item.id,
    label: item.name,
  })),
);

const businessAreaOptions = computed(() =>
  hasFilterField('businessArea')
    ? props.filter.businessArea?.map((item) => ({
      value: item.id,
      label: item.text,
    }))
    : [],
);

const rentalPropertyOptions = computed(() =>
  hasFilterField('rentalProperty')
    ? props.filter.rentalProperty?.map((item) => ({
      value: item.id,
      label: item.name,
    }))
    : [],
);

const clusterOptions = computed(() =>
  hasFilterField('cluster')
    ? props.filter.cluster?.map((item) => ({
      value: item.id,
      label: item.name,
    }))
    : [],
);

const typeEducationOptions = computed(() =>
  hasFilterField('educationType')
    ? props.filter.educationType?.map((item) => ({
      value: item.id,
      label: item.text,
    }))
    : [],
);

function handleSearch(value: string) {
  emit('params-change', { page: 1, filter: filter.value, search: value });
}

function applyFilters() {
  if (dateRange.value) {
    filter.value.dateFrom = dateRange.value[0].format('YYYY-MM-DD');
    filter.value.dateTo = dateRange.value[1].format('YYYY-MM-DD');
  } else {
    filter.value.dateFrom = null;
    filter.value.dateTo = null;
  }

  filterOpen.value = false;

  emit('params-change', {
    page: 1,
    filter: { ...filter.value },
    search: search.value,
  });
}

function resetFilters() {
  dateRange.value = undefined;

  filter.value = {
    status: '',
    dateFrom: null,
    dateTo: null,
    businessArea: '',
    rentalProperty: '',
    cluster: '',
    educationType: '',
  };

  filterOpen.value = false;

  emit('params-change', {
    page: 1,
    filter: { ...filter.value },
    search: search.value,
  });
}

function handlePageChange(page: number) {
  emit('params-change', { page: page, filter: filter.value, search: search.value });
}

function onRowClick(event: any) {
  const data = event.data;
  emit('row-click', data);
}

function onExportClick() {
  emit('export-click', { filter: filter.value, search: search.value });
}

function getRequestRowClass(request: RawApplication, noWrap?: boolean) {
  const baseClass = 'personal-area-table__cell-content';
  const newStatusClass =
    request.status.code === 'new' ? ' personal-area-table__cell-content--new' : '';
  const noWrapClass = noWrap ? ' personal-area-table__cell-content--nowrap' : '';
  return baseClass + newStatusClass + noWrapClass;
}

const getRowClass = (data: RawApplication) => ({
  'personal-area-table__row-new': data.status.code === 'new',
});

function getStatusClass(status: ApiStatus | null): string {
  return `personal-area-table__status personal-area-table__status--${String(status)
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')}`;
}
</script>

<style>
.request-filter-drawer .ant-picker-range {
  .ant-picker-range-separator {
    padding: 0 4px;
    color: var(--stc-gray-300);
  }

  .ant-picker-input input {
    text-align: center;
  }

  .ant-picker-active-bar {
    display: none;
  }
}

.ant-picker-range:hover .ant-picker-clear {
  opacity: 1;
}

.ant-picker-range:hover:has(.ant-picker-clear) .ant-picker-suffix {
  opacity: 0;
}

.request-filter-drawer .ant-drawer-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.request-filter-drawer .request-filter-drawer__close-trigger {
  display: flex;
  justify-content: flex-end;
}

.request-filter-drawer .ant-form {
  width: 100%;
}

.request-filter-drawer .ant-drawer-header-title {
  flex-direction: row-reverse;
}

.request-filter-drawer__header-shell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.request-filter-drawer__header-overline {
  font-size: 24px;
  font-family: var(--stc-font-semibold), sans-serif;
  color: var(--stc-black);
}

.request-filter-drawer .ant-drawer-footer {
  padding: 30px 20px;
}
</style>
